import { useState, useEffect } from "react";
import { useApprovals } from "../hooks/useApprovals";
import { useAuth } from "../contexts/AuthContext";
import { can } from "../lib/permissions";
import { api } from "../lib/api";
import { Loader2, Check, X as XIcon, Clock, ShieldCheck } from "lucide-react";
import { LoadingIndicator } from "../components/ShellContentFrame";
import { toast } from "sonner";

const TITLES = {
    percentage_change: "Update allocations",
    member_removal: "Remove recipient",
    role_change: "Change role",
    schedule_change: "Change payout schedule",
    payout_execution: "Execute payout",
};

function relTime(iso) {
    try {
        const ms = Date.now() - new Date(iso).getTime();
        const m = Math.round(ms / 60000);
        if (m < 1) return "just now";
        if (m < 60) return `${m}m ago`;
        const h = Math.round(m / 60);
        if (h < 24) return `${h}h ago`;
        return `${Math.round(h / 24)}d ago`;
    } catch (_e) { return iso; }
}

function ApprovalCard({ a, canDecide, onDecided }) {
    const [busy, setBusy] = useState(null);
    const decide = async (decision) => {
        try {
            setBusy(decision);
            const { data } = await api.post(`/approvals/${a.id}/${decision}`, { note: "" });
            toast.success(decision === "approve" ? "Approved" : "Rejected");
            onDecided?.(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        } finally { setBusy(null); }
    };

    return (
        <li className="surface p-4 sm:p-5" data-testid={`approval-${a.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap fs-pill-anchor">
                        <span className="fs-pill gap-1 rounded-full bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 text-[10px] font-semibold text-amber-300 uppercase tracking-wider">
                            <Clock className="h-3 w-3" /> Pending
                        </span>
                        <h3 className="font-heading text-lg">{TITLES[a.type] || a.title}</h3>
                    </div>
                    <p className="text-xs text-white/45 mt-1">
                        Requested by <span className="text-white/75">{a.requested_by_name || a.requested_by_email}</span> · {relTime(a.created_at)}
                    </p>
                    <div className="mt-3 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3 text-xs font-mono text-white/70 overflow-auto max-h-40" data-testid={`approval-payload-${a.id}`}>
                        <pre className="whitespace-pre-wrap">{JSON.stringify(a.payload, null, 2)}</pre>
                    </div>
                </div>
                {canDecide && (
                    <div className="flex gap-2 shrink-0">
                        <button onClick={() => decide("reject")} disabled={busy} className="btn-ghost rounded-md px-3 py-1.5 text-xs inline-flex items-center gap-1.5" data-testid={`approval-reject-${a.id}`}>
                            {busy === "reject" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <XIcon className="h-3.5 w-3.5" />}
                            Reject
                        </button>
                        <button onClick={() => decide("approve")} disabled={busy} className="btn-neon rounded-md px-3 py-1.5 text-xs inline-flex items-center gap-1.5" data-testid={`approval-approve-${a.id}`}>
                            {busy === "approve" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                            Approve
                        </button>
                    </div>
                )}
            </div>
        </li>
    );
}

function HistoryCard({ a }) {
    const isApproved = a.status === "approved";
    return (
        <li className="surface p-4 opacity-90" data-testid={`approval-history-${a.id}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap fs-pill-anchor">
                        <span className={`fs-pill gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border ${
                            isApproved ? "bg-[#00BF0D]/10 border-[#00BF0D]/20 text-[#00BF0D]" : "bg-rose-500/10 border-rose-500/25 text-rose-300"
                        }`}>
                            {isApproved ? <Check className="h-3 w-3" /> : <XIcon className="h-3 w-3" />} {a.status}
                        </span>
                        <p className="text-sm leading-none">{TITLES[a.type] || a.title}</p>
                    </div>
                    <p className="text-xs text-white/40 mt-1">
                        by {a.requested_by_name} · {relTime(a.decided_at || a.created_at)}
                    </p>
                </div>
            </div>
        </li>
    );
}

export function ApprovalsPage() {
    const { activeOrg } = useAuth();
    const role = activeOrg?.role || "member";
    const canDecide = can.approve(role);
    const pending = useApprovals("pending");
    const recent = useApprovals(""); // all, will filter
    const [completed, setCompleted] = useState([]);

    useEffect(() => {
        if (recent.approvals) {
            setCompleted(recent.approvals.filter((a) => a.status !== "pending").slice(0, 15));
        }
    }, [recent.approvals]);

    return (
        <div className="space-y-7">
            <div>
                <p className="label-eyebrow">Workflow</p>
                <h1 className="font-heading text-3xl sm:text-4xl tracking-tight mt-1">Approvals</h1>
                <p className="text-white/50 mt-1.5 max-w-2xl">
                    High-impact changes (allocations, payouts, role changes, scheduling) require co-sign from an Admin or Owner before applying.
                </p>
                <div className="mt-3"><span className="fs-pill gap-2 px-3 py-1.5 rounded-full border border-white/[0.08] bg-white/[0.025] text-xs text-white/70"><ShieldCheck className="h-3.5 w-3.5 text-[#00BF0D]" /> Every decision is logged in the audit trail.</span></div>
            </div>

            <section className="relative min-h-[8rem]">
                <h2 className="label-eyebrow mb-3">Pending</h2>
                <div className={pending.loading ? "pointer-events-none invisible" : ""} aria-hidden={pending.loading}>
                    {pending.approvals.length === 0 ? (
                        <div className="surface p-8 text-center text-white/45" data-testid="approvals-empty">
                            <ShieldCheck className="h-6 w-6 text-[#00BF0D]/70 mx-auto mb-2" />
                            Nothing pending. You're all clear.
                        </div>
                    ) : (
                        <ul className="space-y-3">
                            {pending.approvals.map((a) => (
                                <ApprovalCard key={a.id} a={a} canDecide={canDecide && a.requested_by_email !== activeOrg?.email} onDecided={() => { pending.refetch(); recent.refetch(); }} />
                            ))}
                        </ul>
                    )}
                </div>
                {pending.loading && (
                    <div className="absolute inset-0 top-6 grid place-items-center">
                        <LoadingIndicator label="Loading approvals…" testId="approvals-pending-loading" />
                    </div>
                )}
            </section>

            {completed.length > 0 && (
                <section>
                    <h2 className="label-eyebrow mb-3">Recent decisions</h2>
                    <ul className="space-y-2">
                        {completed.map((a) => <HistoryCard key={a.id} a={a} />)}
                    </ul>
                </section>
            )}
        </div>
    );
}
